import type { Oportunidade } from './tipos';

/**
 * Lista de 30/10 (B), item 7.1: o ponto único do "pode editar?" da negociação na tela (ficha em página e gaveta). Toda edição
 * no lugar, os cartões e as abas perguntam aqui, em vez de cada um repetir `pode && aberta`.
 *
 * - `dados`: os campos da negociação (nome, previsão, regime, fonte, campanha, campos personalizados, contatos, empresa,
 *   vertical, responsável, playbook). Precisa de `pipeline.editar` e da negociação aberta (a ganha e a perdida só reabrindo).
 * - `atividades`: o que segue valendo depois de encerrada (tarefas, e-mail, questionários, produtos, arquivos e propostas, como
 *   antes). Precisa de `pipeline.editar` e, desde o item 8, da negociação fora da pausa.
 * - `trava`: por que não dá (para o aviso da tela), `null` quando dá.
 *
 * Item 8 da mesma lista: a negociação pausada não se altera antes de ser retomada. A trava `pausada` vive AQUI e a tela inteira
 * respeita (ficha, gaveta, cartões e abas); só "Retomar" fica. O servidor confere à parte (`oportunidade.pausada`; a trava da
 * tela não é segurança).
 */
export type TravaNegociacao = 'sem_permissao' | 'encerrada' | 'pausada' | null;

export interface EdicaoNegociacao {
  dados: boolean;
  atividades: boolean;
  trava: TravaNegociacao;
}

type Estado = Pick<Oportunidade, 'estado'> & { pausada_em?: string | null };

/** Por que os dados da negociação não se editam (função pura, testada). */
export function travaDaNegociacao(o: Estado, podeEditar: boolean): TravaNegociacao {
  if (!podeEditar) return 'sem_permissao';
  if (o.estado !== 'aberta') return 'encerrada';
  if (o.pausada_em) return 'pausada';
  return null;
}

/** O que a pessoa pode editar na negociação, pela permissão `pipeline.editar` (`s.pode('pipeline.editar')`) e pelo estado. */
export function edicaoDaNegociacao(o: Estado, podeEditar: boolean): EdicaoNegociacao {
  const trava = travaDaNegociacao(o, podeEditar);
  return { dados: trava === null, atividades: podeEditar && trava !== 'pausada', trava };
}

/**
 * Lista de 02/11, item 6 (rl0211-2 SPEC-M1): o "Criar tarefa" da negociação grava em `POST /pipeline/atividades`, que exige
 * `pipeline.criar` (não `pipeline.editar`). Aparece com a negociação aberta e fora da pausa; a permissão é a MESMA da rota.
 */
export function podeCriarTarefaNaNegociacao(o: Estado, podeCriar: boolean): boolean {
  return podeCriar && o.estado === 'aberta' && !o.pausada_em;
}
